import { useCallback, useRef, useMemo } from 'react'
import {
  getFirAuth,
  getGoogleAuthCredential,
  getGoogleProvider,
} from '../../Api/firebase/firebaseConnect'
import {
  signInWithCredential,
  signInWithPopup,
  getBrowserPopupRedirectResolver,
} from '../../Api/firebase/auth'
import { GOOGLE_CLIENT_ID } from '../../Config/Firebase/config'
import { checkIfEmbededInNativeApp, handleAuthError, logEvent } from '../../Utils'
import {
  ANALYTICS_TAGS,
  GOOGLE_CONSOLE_MESSAGES,
  GOOGLE_ERROR_MESSAGES,
} from '../../Constant'
import type { User } from '../../Api/Users/types'
import type { GoogleCredential, GoogleAuthOperations } from './types'

export const useGoogleAuth = (
  toggleShowSnackbar: (snackbarData: {
    text: string
    color: 'red' | 'blue' | 'green'
  }) => void,
  setShowLogin: (show: { show: boolean; extraFields: string[] }) => void
): GoogleAuthOperations => {
  const lastPromptTimeRef = useRef<number>(0)
  const promptCooldownMs = 10000 // 10 seconds cooldown
  const isPromptActiveRef = useRef<boolean>(false)
  const isGSIInitializedRef = useRef<boolean>(false)
  const handleTokenRef = useRef<((token: GoogleCredential) => Promise<void>) | null>(
    null
  )

  const handleGoogleOneTapPrompt = useCallback(
    (usr: User) => {
      const isEmbedded = checkIfEmbededInNativeApp()
      if (isEmbedded) {
        // Don't show one tap in native app
        window.google?.accounts?.id?.cancel()
        return
      }

      if (!window?.google?.accounts?.id) {
        console.warn(GOOGLE_CONSOLE_MESSAGES.SCRIPT_NOT_LOADED)
        return
      }

      if (!GOOGLE_CLIENT_ID) {
        console.warn(GOOGLE_CONSOLE_MESSAGES.CLIENT_ID_MISSING)
        return
      }

      const now = Date.now()
      const timeSinceLastPrompt = now - lastPromptTimeRef.current

      if (!usr.isLoggedIn || usr.isAnonymous) {
        if (timeSinceLastPrompt < promptCooldownMs || isPromptActiveRef.current) return
        lastPromptTimeRef.current = now
        isPromptActiveRef.current = true

        try {
          window.google.accounts.id.prompt(() => {
            isPromptActiveRef.current = false
          })
        } catch (error) {
          isPromptActiveRef.current = false
          console.error(GOOGLE_CONSOLE_MESSAGES.ONE_TAP_ERROR, error)
          handleAuthError(
            ANALYTICS_TAGS.auth_change_error,
            error,
            'handleGoogleOneTapPrompt'
          )
        }
        return
      }

      if (usr.isLoggedIn && !usr.isAnonymous) {
        window.google?.accounts?.id?.cancel()
        isPromptActiveRef.current = false
      }
    },
    [promptCooldownMs]
  )

  const handleToken = useCallback(async (token: GoogleCredential): Promise<void> => {
    try {
      const auth = await getFirAuth()
      if (!auth) {
        handleAuthError(
          ANALYTICS_TAGS.auth_change_error,
          'Firebase auth not available',
          'handleToken'
        )
        return
      }

      const cred = await getGoogleAuthCredential(token.credential)
      if (!cred) {
        handleAuthError(
          ANALYTICS_TAGS.auth_change_error,
          'Failed to get Google auth credential',
          'handleToken'
        )
        return
      }

      await signInWithCredential(auth, cred)
    } catch (error) {
      console.error(GOOGLE_CONSOLE_MESSAGES.TOKEN_ERROR, error)
      handleAuthError(ANALYTICS_TAGS.auth_change_error, error, 'handleToken')
      toggleShowSnackbar({
        text: GOOGLE_ERROR_MESSAGES.GENERIC_ERROR,
        color: 'red',
      })
    }
  }, [])

  handleTokenRef.current = handleToken

  const initializeGSI = useCallback(() => {
    try {
      const isEmbedded = checkIfEmbededInNativeApp()
      if (isEmbedded) return
      if (isGSIInitializedRef.current) return

      if (!window?.google?.accounts) {
        console.warn(GOOGLE_CONSOLE_MESSAGES.SCRIPT_NOT_LOADED)
        return
      }

      if (!GOOGLE_CLIENT_ID) {
        console.warn(GOOGLE_CONSOLE_MESSAGES.CLIENT_ID_MISSING)
        logEvent(ANALYTICS_TAGS.google_config_missing, {
          error: 'Missing GOOGLE_CLIENT_ID environment variable',
          product: 'web',
        })
        return
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (token: GoogleCredential) => {
          if (handleTokenRef.current) {
            handleTokenRef.current(token)
          }
        },
        use_fedcm_for_prompt: true,
        auto_select: false,
        cancel_on_tap_outside: true,
        context: 'signin',
      })
      isGSIInitializedRef.current = true
    } catch (error) {
      console.error(GOOGLE_CONSOLE_MESSAGES.INITIALIZATION_ERROR, error)
      handleAuthError(ANALYTICS_TAGS.auth_change_error, error, 'initializeGSI')
    }
  }, [])

  const signInWithGooglePopup = useCallback(async (): Promise<void> => {
    try {
      const auth = await getFirAuth()
      if (!auth) {
        throw new Error('Firebase auth not available')
      }

      const provider = await getGoogleProvider()
      if (!provider) {
        throw new Error('Google provider not available')
      }

      const resolver = await getBrowserPopupRedirectResolver()
      const result = await signInWithPopup(auth, provider, resolver)

      if (result?.user?.uid) {
        setShowLogin({ show: false, extraFields: [] })
      }
    } catch (error) {
      console.error(GOOGLE_CONSOLE_MESSAGES.POPUP_ERROR, error)
      handleAuthError(ANALYTICS_TAGS.login_error, error, 'signInWithGooglePopup', {
        provider: 'google',
      })
      setShowLogin({ show: false, extraFields: [] })
      throw error
    }
  }, [setShowLogin])

  return useMemo(
    () => ({
      initializeGSI,
      handleGoogleOneTapPrompt,
      handleToken,
      signInWithGooglePopup,
    }),
    [initializeGSI, handleGoogleOneTapPrompt, handleToken, signInWithGooglePopup]
  )
}
